import styles from './CheckInForm.module.scss';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Button from 'components/Button/Button';
import React, { useEffect, useRef, useState } from 'react';
import SelectComponent from 'components/Input/Select';
import { checkInTypeOptions } from '../../variables';
import DateTimeSelect from 'components/Input/DateTimeSelect';
import { store } from 'stores/Store';
import useExternalApiService from 'helpers/ExternalApiService';
import { ReactComponent as CameraIcon } from "assets/camera.svg";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Image, LoadingOverlay, Modal, Overlay } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import Toastr from 'components/Toastr';
import { v4 as uuidv4 } from "uuid";
import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK
import useOnlineStatus from 'hooks/useOnlineStatus';



interface ICheckInForm {
  navigateBack: () => void;
  selectedAddress: any;
  onCheckinAdded?: () => void;
  selectedCheckIn?: any;
}

const CheckInForm = ({ navigateBack, selectedAddress, onCheckinAdded, selectedCheckIn }: ICheckInForm) => {
  const [selectedDateTime, setSelectedDateTime] = React.useState<string>(() => {
    const now = new Date();
    // Get the raw time without timezone adjustments
    return new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes()
    ).toISOString();
  });
  const [notes, setNotes] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [practitioners, setPractitioners] = React.useState<any[]>([]);

  // const [selectedPractitioner, setSelectedPractitioner] = React.useState<any>(null);
  const [selectedCheckInType, setSelectedCheckInType] = React.useState<any>(checkInTypeOptions[0]);
  const [selectedFollowUpDate, setSelectedFollowUpDate] = React.useState<string>(new Date().toISOString());
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const [isAddingCheckin, setIsAddingCheckin] = useState(false);
  const { postData, deleteData } = useExternalApiService(apiUrl);

  // Photo-related state
  const [showPhotoModal, setShowPhotoModal] = React.useState(false);
  const [showCamera, setShowCamera] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [capturedImage, setCapturedImage] = React.useState<string | null>(null);
  const [showCarouselModal, setShowCarouselModal] = React.useState(false);
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const [photos, setPhotos] = React.useState<{ id: number | string, url: string, api_url?: string, imgObject?: HTMLImageElement, dataUrl?: string }[]>([]);
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [tmpId, setTmpId] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const isOnline = useOnlineStatus();
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  // just to be used inside the "clean up return of useEffect"
  const handleSaveDraftCbRef = useRef((checkIn: any) => {});

  // Verification-related state
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isCheckingLocation, setIsCheckingLocation] = useState(true);

  // Refs for photo functionality
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Amplitude: helper to track all button click submission to AI
  const trackClick = (buttonName: string) => {
    const label = buttonName
    logEvent(label, { buttonName });
  };

  // Verification handling function - checks if user is within 5 miles of address
  const checkVerificationStatus = async () => {
    setIsCheckingLocation(true);

    // If offline, mark as unverified immediately
    if (!isOnline) {
      setIsVerified(false);
      setIsCheckingLocation(false);
      return;
    }

    if ("geolocation" in navigator) {
      try {
        const distance = await new Promise<number>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition((position) => {
            const lat1 = position.coords.latitude;
            const lon1 = position.coords.longitude;
            const lat2 = selectedAddress?.latitude;
            const lon2 = selectedAddress?.longitude;

            // Calculate distance using Haversine formula
            const R = 6371; // Radius of earth in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;

            const a =
              Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = R * c;

            resolve(Math.round(distance * 0.621371)); // Convert to miles and round
          }, (error) => {
            reject(error);
          });
        });

        setIsVerified(distance <= 5);
      } catch (error) {
        console.error('Error getting location:', error);
        setIsVerified(false);
      }
    } else {
      setIsVerified(false);
    }

    setIsCheckingLocation(false);
  };

  // Photo handling functions
  const handleShowToast = (message: string) => {
    setShowToast(true);
    setToastMessage(message);
  };

  const handlePhotoUpload = () => {
    setShowPhotoModal(true);
  };

  const handlePhotoClick = (index: number) => {
    if (photos.length > 0 && !isLoadingPhotos) {
      setCarouselIndex(index);
      setShowCarouselModal(true);
    }
  };

  const handleGallerySelect = () => {
    setShowPhotoModal(false);
    setShowCamera(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraSelect = async () => {
    setShowPhotoModal(false);
    setCapturedImage(null);
    setShowCamera(true);

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const constraints = {
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Cannot access camera, fallback to file input", err);
        handleShowToast('Camera access denied. Using gallery instead.');
        setShowCamera(false);
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    } else {
      console.warn("MediaDevices not supported, fallback to file input");
      handleShowToast('Camera not supported. Using gallery instead.');
      setShowCamera(false);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL("image/png");

      const stream = video.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());
      setShowCamera(false);

      await addPhotoToList(imageUrl);
    }
  };

  const uploadImageToAPI = async (imageUrl: string, checkinId: number) => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();

      const sasResponse = await postData('/api/get_azure_sas', {
        fmc_id: store.user?.userName || '',
        practitioner_id: store.currentPractitioner.id,
        checkin_id: checkinId,
        filename: `checkin_${Date.now()}.jpg`
      });

      const { blob_url } = sasResponse;

      const headers = new Headers();
      headers.append('x-ms-blob-type', 'BlockBlob');

      const uploadResponse = await fetch(blob_url, {
        method: 'PUT',
        headers,
        body: blob
      });

      if (!uploadResponse.ok) {
        throw new Error(`Azure upload failed with status ${uploadResponse.status}`);
      }

      return { success: true, blob_url };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const addPhotoToList = (imageUrl: string) => {
    if (photos.length >= 10) {
      handleShowToast('Maximum of 10 photos allowed');
      return;
    }

    const newPhoto = {
      id: photos.length + 1,
      url: imageUrl,
      dataUrl: imageUrl
    };
    setPhotos(prev => [...prev, newPhoto]);
    setCapturedImage(null);

    handleShowToast('Photo added successfully');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      await addPhotoToList(url);
    }
    e.target.value = '';
  };

  const handleDeletePhoto = async () => {
    console.log("handleDeletePhoto", carouselIndex);
    
    const photoToDelete = photos[carouselIndex];
    if (!photoToDelete) return;
    if (!selectedCheckIn?.id) return;
    
    try {
      // Call API to delete image from Azure
      await postData('/api/delete_image_from_azure', {
        fmc_id: store.user?.userName || '',
        practitioner_id: store.currentPractitioner.id,
        checkin_id: selectedCheckIn.id,
        image_url: photoToDelete.api_url || photoToDelete.url
      });
      
      // Remove photo from local state using the photo's unique ID
      const remainingPhotos = photos.filter((_, i) => i !== carouselIndex);
      setPhotos(remainingPhotos);
      
      // Close modal if no photos left
      if (remainingPhotos.length === 0) {
        setShowCarouselModal(false);
      } else {
        // Adjust carousel index if needed
        if (carouselIndex >= remainingPhotos.length) {
          setCarouselIndex(remainingPhotos.length - 1);
        }
      }
      
      handleShowToast('Photo deleted successfully');
    } catch (error) {
      console.error('Failed to delete photo:', error);
      handleShowToast('Failed to delete photo');
    }
  };

  const handleSave = async () => {
    setIsAddingCheckin(true);

    // Use the already-calculated verification status
    const verified = isVerified ?? false;

    trackClick('Successfully Submitted Checkin');

    try {
      let checkInResult;
      if (selectedCheckIn?.id) {
        checkInResult = await postData('/api/update_checkin', {
          checkin_id: selectedCheckIn.id.toString(),
          checkin_data: {
            "notes": notes,
            "follow_up_date": selectedFollowUpDate,
            "type": selectedCheckInType.value,
            "verified": verified,
            "checkin_address": selectedAddress?.id,
            "status": "submitted"
          }
        });
      } else {
        checkInResult = await postData('/api/add_checkin', {
          "practitioner_id": store.currentPractitioner.id,
          "checkin_data": {
            "type": selectedCheckInType.value,
            "notes": notes,
            "follow_up_date": selectedFollowUpDate,
            "verified": verified,
            "checkin_address": selectedAddress?.id,
            "status": "submitted"
          }
        });
      }

      // If we have photos and a check-in was created successfully, upload them
      if (photos.length > 0 && checkInResult?.checkin_id) {
        try {
          // Upload photos one by one
          for (const photo of photos) {
            if (photo?.api_url) {
              continue
            }
            await uploadImageToAPI(photo.url, checkInResult.checkin_id);
          }
          handleShowToast('Check-in and photos saved successfully');
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          handleShowToast('Check-in saved but some photos may not have uploaded');
        }
      } else if (photos.length === 0) {
        handleShowToast('Check-in saved successfully');
      }
      cleanUpAutoRecovery();
      onCheckinAdded?.();
    } catch (err) {
      console.error(err);
      handleShowToast('Error saving check-in');
    }

    setIsAddingCheckin(false);
    navigateBack();
  }

  const handleSaveDraft = async (checkIn: any) => {
    setIsAddingCheckin(true);

    try {
      let checkInResult;
      if (checkIn?.id) {
        checkInResult = await postData('/api/update_checkin', {
          checkin_id: checkIn.id + "",
          checkin_data: {
            "notes": notes,
            "follow_up_date": selectedFollowUpDate,
            "type": selectedCheckInType.value,
          }
        });
        checkInResult = {
          ...checkInResult,
          checkin_id: checkIn.id,
        };
      } else {
        checkInResult = await postData('/api/add_checkin', {
          "practitioner_id": store.currentPractitioner.id,
          "checkin_data": {
            "type": selectedCheckInType.value,
            "notes": notes,
            "follow_up_date": selectedFollowUpDate,
            "verified": false,
            "checkin_address": selectedAddress?.id,
            "status": "draft"
          }
        });
      }
      // If we have photos and a check-in was created successfully, upload them
      if (photos.length > 0 && checkInResult?.checkin_id) {
        try {
          // Upload photos one by one and skip photos already uploaded
          for (const photo of photos) {
            if (photo?.api_url) {
              continue
            }
            await uploadImageToAPI(photo.url, checkInResult.checkin_id);
          }
          handleShowToast('Check-in and photos saved successfully');
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          handleShowToast('Check-in saved but some photos may not have uploaded');
        }
      } else if (photos.length === 0) {
        handleShowToast('Check-in saved successfully');
      }
      cleanUpAutoRecovery();
      onCheckinAdded?.();
    } catch (err) {
      console.error("Error creating checkin drafts", err);
      handleShowToast('Error saving check-in draft');
    }
    setIsAddingCheckin(false);
    navigateBack();
  }
  handleSaveDraftCbRef.current = handleSaveDraft;

  const cleanUpAutoRecovery = () => {
    if (selectedCheckIn?.id) {
      localStorage.removeItem(
        `tmpNotes_${store.currentPractitioner.id}_${selectedCheckIn.id + ""}`
      );
    } else {
      localStorage.removeItem(
        `tmpNotes_${store.currentPractitioner.id}_${tmpId}`
      );
    }
  }

  const handleDeleteDraft = (checkinId: string) => {
    deleteData(`/api/checkins/${checkinId}`)
      .then(() => {
        handleShowToast('Check-in deleted');
        cleanUpAutoRecovery();
      })
      .finally(() => {

        setIsAddingCheckin(false);
        navigateBack();
      })
  }


  React.useEffect(() => {
    if (store.checkIn) {
      setNotes(store.checkIn.notes ?? '');
      
    }
    if (!selectedAddress || !selectedAddress?.latitude 
      || !selectedAddress?.longitude) {
      handleShowToast("Please set the default check-in address if you want to get a geo-verified check-in.");
    }

    return () => {
      Object.keys(localStorage).forEach((k) => {
        if (k.includes(`tmpNotes_${store.currentPractitioner.id}`)) {
          const ciRaw = localStorage.getItem(k);
          if (ciRaw && !selectedCheckIn?.is_local) {
            const ci = JSON.parse(ciRaw);
            handleSaveDraftCbRef.current({ id: ci?.id });
          }
        }
      })
    }
    // getPractitioners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (selectedCheckIn) {
      setNotes(selectedCheckIn.notes ?? '');
      setSelectedCheckInType({ value: selectedCheckIn.type, label: checkInTypeOptions.find(opt => opt.value === selectedCheckIn.type)?.label });
      if (selectedCheckIn?.tmpId) {
        setTmpId(selectedCheckIn.tmpId);
      }
      if (selectedCheckIn?.status === "draft") {
        setIsDraft(true);
      }
    } else {
      setTmpId(uuidv4());
    }

    //images
    const fetchImages = async () => {
      try {
        setIsLoadingPhotos(true);
        const result = await postData('/api/get_images_from_azure', {
          fmc_id: store.user?.userName || '',
          practitioner_id: store.currentPractitioner.id,
          checkin_id: selectedCheckIn.id,
        });

        if (Array.isArray(result?.images)) {
          // Preload each image and convert to data URL for offline use
          const loadImage = (url: string) =>
            new Promise<{img: HTMLImageElement, dataUrl: string}>((resolve, reject) => {
              const img = new window.Image();
              img.crossOrigin = 'anonymous'; // Enable CORS for canvas operations
              img.src = url;
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  canvas.width = img.width;
                  canvas.height = img.height;
                  ctx.drawImage(img, 0, 0);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  resolve({img, dataUrl});
                } else {
                  reject(new Error('Could not get canvas context'));
                }
              };
              img.onerror = reject;
            });
  
          const loadedImages = await Promise.all(
            result.images.map((url: string, idx: number) =>
              loadImage(url).then(({img, dataUrl}) => ({
                id: idx + 1,
                url: dataUrl, 
                api_url: url, 
                imgObject: img,
                dataUrl: dataUrl, 
              }))
            )
          );
          setPhotos(loadedImages);
        }
      } catch (error) {
        console.error('Failed to fetch images from Azure:', error);
      } finally {
        setIsLoadingPhotos(false);
      }
    };
    if (selectedCheckIn?.id) {
      fetchImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCheckIn]);

  useEffect(() => {
    if (notes) {
      if (selectedCheckIn?.id) {
        localStorage.setItem(
          `tmpNotes_${store.currentPractitioner.id}_${selectedCheckIn.id + ""}`,
          JSON.stringify({
            notes: notes,
            follow_up_date: selectedFollowUpDate,
            type: selectedCheckInType.value,
            selectedAddressId: selectedAddress?.id,
            id: selectedCheckIn.id,
          }),
        );
      } else {
        localStorage.setItem(
          `tmpNotes_${store.currentPractitioner.id}_${tmpId}`,
          JSON.stringify(
            {
              notes: notes,
              follow_up_date: selectedFollowUpDate,
              type: selectedCheckInType.value,
              selectedAddressId: selectedAddress?.id,
              tmpId: tmpId,
            }),
        );
      }
    }
  }, [notes, selectedFollowUpDate, selectedCheckInType.value, selectedCheckIn, selectedAddress?.id, tmpId])

  // Check verification status when address changes or when coming back online
  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress, isOnline]);

  // const getPractitioners = async () => {
  //   try {
  //     const result = await postData('/api/get_practitioners_for_chat', {"fmc_email": store.impersonatedUser});
  //     const formattedPractitioners = result.practitioners.map((pract: any) => ({
  //       label: pract.primary_name,
  //       value: pract.id,
  //     }));

  //     setPractitioners(formattedPractitioners);    
  //     // Only look for the practitioner after we have the data
  //     const practitioner = result.practitioners.find((p: any) => {
  //       return p.id === store.currentPractitioner?.id;
  //     });
  //     if (practitioner) {
  //       setSelectedPractitioner({
  //         label: practitioner.primary_name,
  //         value: practitioner.id,
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error fetching practitioners:', error);
  //   }
  // };

  return (
    <div className={styles.wrapper}>
      {/* Verification Status Banner */}
      {!isCheckingLocation && isVerified !== null && (
        <div>
          {isVerified ? (
            <div className={styles.verifiedWrapper}>
              <p className={styles.verifiedText}>Verified Check-In</p>
            </div>
          ) : (
            <div className={styles.unverifiedWrapper}>
              <p className={styles.unverifiedText}>Unverified Check-In</p>
            </div>
          )}
        </div>
      )}

      <div className={styles.headerWrapper}>
        <p className={styles.headerText}>Check-In for: </p>
        <div className={styles.btnsWrapper}>
          {/* <Button
            color="#00C542"
            textColor="white"
            borderColor="#00C54280"
            type="outline"
            onClick={() => {
              if (!isAddingCheckin) {
                handleSave();
              }
            }}
          >
            SAVE
          </Button> */}
          {/* <Button type="outline" onClick={() => handleSaveDraft(selectedCheckIn)}>Cancel</Button> */}
        </div>
      </div>

      <div className={styles["current-pract"]}>{store.currentPractitioner.primary_name}</div>
      {/* <SelectComponent
        value={{label: "Mona Solar", value: "Mona Solar"}}
        onChange={() => {}}
        options={[{label: "Mona Solar", value: "Mona Solar"}]} 
        placeholder="Select a Practitioner" 
      /> */}
      <p className={styles.label}>Time</p>
      <DateTimeSelect
        value={selectedDateTime}
        setValue={(e: string) => {
          setSelectedDateTime(e);
          setSelectedFollowUpDate(e);
        }}
      />
      <p className={styles.label}>Log Type</p>
      <SelectComponent options={checkInTypeOptions} placeholder="Select a Type" value={selectedCheckInType} onChange={(e: any) => setSelectedCheckInType(e)} />
      <p className={styles.label}>Notes</p>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className={styles.textarea}
        placeholder="Enter your notes here..."
      />



      <div className={styles.headerRow}>
        <p style={{ fontWeight: '800' }} className={styles.label}>Photos</p>
        <div className={styles.label}>You can upload up to 10 photos</div>
      </div>

      <div className={styles.photoUploadSection}>
        {isLoadingPhotos && (
          <div className={styles.photoLoadingContainer}>
            <div className={styles.photoLoadingPlaceholder}>
              <div className={styles.loadingSpinner}></div>
              <span className={styles.loadingText}>Loading photos...</span>
            </div>
          </div>
        )}
        {!isLoadingPhotos && (
          <>
            <div className={styles.photosGrid}>
              {photos.length < 10 && (
                <div className={styles.photoUploadCard} onClick={handlePhotoUpload}>
                  <div className={styles.cameraIcon}>
                    <CameraIcon />
                  </div>
                  <div className={styles.uploadText}>Add Check-In Photo</div>
                </div>
              )}

              {photos.map((photo, index) => (
                <div key={photo.id} className={styles.photoCard} onClick={() => handlePhotoClick(index)}>
                  <img
                    src={photo.dataUrl || photo.url}
                    alt={`Check-in ${photo.id}`}
                    className={styles.photoImage}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className={styles.photoPlaceholder} style={{ display: 'none' }}>
                    <span className={styles.photoText}>Photo {photo.id}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>

      <div className={styles.buttonsContainer}>
        <button
          type="button"
          style={{
            backgroundColor: isOnline ? "#2bc35e" : "#ccc"
          }}
          className={styles.primaryButton}
          onClick={() => {
            if (!isAddingCheckin && isOnline) {
              handleSave();
            }
          }}
        >
          {isAddingCheckin && (
            <>
              <span className={styles["loading-button__loader"]}></span>
              <span className={styles["loading-button__loader-cover"]}></span>
            </>
          )}
          Save
          <span>CHECK-IN NOW</span>
        </button>
        <button type="button" className={styles.secondaryButton} onClick={() => { if (!isAddingCheckin) { handleSaveDraft(selectedCheckIn); } }}>
          Save Draft
          <span>CHECK-IN LATER</span>
        </button>
      </div>
      {selectedCheckIn?.id && isDraft ? (
        <button type="button" className={styles.secondaryButton}
          style={{
            backgroundColor: "#E64B37",
            marginRight: "10px",
            marginLeft: "10px",
          }}
          onClick={() => { if (!isAddingCheckin) { handleDeleteDraft(selectedCheckIn?.id); } }}>
          Discard Draft
        </button>
      ) : null}

      {/* <p className={styles.label}>Follow-Up Date</p>
      <DateTimeSelect value={selectedFollowUpDate} setValue={setSelectedFollowUpDate}/> */}

      <Toastr
        backgroundColor="white"
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* Full-screen loading overlay */}
      <LoadingOverlay visible={isAddingCheckin}
        zIndex={9999999}
        overlayProps={{
          backgroundOpacity: 0.8,
        }}
        loaderProps={{
          children: (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingContent}>
                <div className={styles.loadingSpinner}></div>
                <div className={styles.loadingText}>Saving check-in...</div>
              </div>
            </div>
          )
        }}/>

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Camera Preview */}
      {showCamera && (
        <div className={styles.cameraPreview}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className={styles.cameraVideo}
          />
          <button className={styles.captureButton} onClick={takePhoto}>
            <CameraIcon width={40} height={40} />
          </button>
        </div>
      )}

      {/* Canvas for capturing photo */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      <ConfirmationModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onConfirm={() => { setShowPhotoModal(false) }}
        title="Photo Source"
        message=""
        cancelText="CANCEL"
        confirmText="DONE"
        modalBodyStyle={{ padding: '10px 0px 10px 0px' }}
        modalContentStyle={{ maxWidth: '400px', width: '100%' }}
        modalHeaderStyle={{ padding: 'margin: 0px' }}
        customRender={
          <div className={styles.photoOptions}>
            <div className={`${styles.photoOption} ${styles.galleryOption}`} onClick={handleGallerySelect}>
              Gallery
            </div>
            <div className={styles.photoDivider}></div>
            <div className={`${styles.photoOption} ${styles.cameraOption}`} onClick={handleCameraSelect}>
              Camera
            </div>
          </div>
        }
      />

      {/* Photo Carousel Modal */}
      <Modal
        opened={showCarouselModal}
        onClose={() => setShowCarouselModal(false)}
        size="lg"
        withCloseButton={true}
        zIndex={5000}
        styles={{
          body: {
            padding: '0',
          },
          content: {
            position: 'fixed',
            bottom: 0,
            top: 'auto',
            margin: 0,
            maxHeight: '100vh',
            width: '100%',
            maxWidth: '500px',
            height: 'auto',
            borderTopLeftRadius: '30px',
            borderTopRightRadius: '30px',
          },
          header: { padding: 15 },
        }}
      >
        <div className={styles.carouselHeader}>
          Check-in Photos
        </div>
        <div className={styles.carouselWrapper}>
          <Carousel
            initialSlide={carouselIndex}
            withIndicators
            withControls={false}
            slideSize="100%"
            slideGap="md"
            onSlideChange={(index) => setCarouselIndex(index)}
            emblaOptions={{
              align: 'start',
              slidesToScroll: 1,
              containScroll: 'trimSnaps',
              loop: false
            }}
            styles={{
              indicators: {
                bottom: -120,
              },
              indicator: {
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255)',
                border: 'none',
                '&[data-active]': {
                  backgroundColor: '#000',
                }
              }
            }}
          >
            {photos.map((photo, index) => (
              <Carousel.Slide key={photo.id}>
                <img
                  src={photo.dataUrl || photo.url}
                  alt={`Check-in ${photo.id}`}
                  className={styles.carouselImage}
                  style={{
                    width: '100%', maxHeight: '350px',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                />
              </Carousel.Slide>
            ))}
          </Carousel>
        </div>
        <div className={styles.deletePhotoButton} onClick={handleDeletePhoto}>
          DELETE PHOTO
        </div>
      </Modal>
    </div>
  )
}

export default CheckInForm;
